import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../App';
import AttachmentsWidget from '../components/AttachmentsWidget';

// ── Constants ────────────────────────────────────────────────────────────────

const LC_TYPES = [
  { value: 'LC',   label: 'Letter of Credit',  icon: '🏦', color: '#065f46', bg: '#ecfdf5', border: '#6ee7b7' },
  { value: 'SBLC', label: 'Standby LC',         icon: '🏛', color: '#1e40af', bg: '#dbeafe', border: '#93c5fd' },
];

const LC_SUBTYPES = [
  { value: 'irrevocable',  label: 'Irrevocable' },
  { value: 'revocable',    label: 'Revocable' },
  { value: 'transferable', label: 'Transferable' },
  { value: 'back_to_back', label: 'Back-to-Back' },
  { value: 'standby',      label: 'Standby' },
];

const STATUS_META = {
  draft:      { label: 'Entwurf',       bg: '#f8fafc', border: '#e2e8f0', text: '#64748b' },
  issued:     { label: 'Ausgestellt',   bg: '#eff6ff', border: '#93c5fd', text: '#1d4ed8' },
  advised:    { label: 'Avisiert',      bg: '#f5f3ff', border: '#c4b5fd', text: '#7c3aed' },
  confirmed:  { label: 'Bestätigt',     bg: '#f0fdf4', border: '#86efac', text: '#15803d' },
  utilized:   { label: 'Abgerufen',     bg: '#fefce8', border: '#fde047', text: '#a16207' },
  expired:    { label: 'Abgelaufen',    bg: '#fef2f2', border: '#fca5a5', text: '#dc2626' },
  cancelled:  { label: 'Storniert',     bg: '#f1f5f9', border: '#cbd5e1', text: '#64748b' },
  amended:    { label: 'Geandert',      bg: '#fff7ed', border: '#fdba74', text: '#c2410c' },
};

const AMENDMENT_STATUS_META = {
  pending:  { label: 'Ausstehend', bg: '#fefce8', border: '#fde047', text: '#a16207' },
  accepted: { label: 'Akzeptiert', bg: '#f0fdf4', border: '#86efac', text: '#15803d' },
  rejected: { label: 'Abgelehnt', bg: '#fef2f2', border: '#fca5a5', text: '#dc2626' },
};

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CHF'];
const INCOTERMS = ['FOB', 'CIF', 'CFR', 'EXW', 'DDP', 'DAP', 'FCA', 'CPT', 'CIP'];

const EMPTY_FORM = {
  lc_type: 'LC', lc_subtype: 'irrevocable',
  reference_number: '', deal_reference: '', title: '',
  applicant_name: '', beneficiary_name: '',
  applicant_bank: '', applicant_bank_swift: '',
  issuing_bank: '', issuing_bank_swift: '',
  advising_bank: '', advising_bank_swift: '',
  confirming_bank: '', confirming_bank_swift: '',
  amount: '', currency: 'USD', tolerance_pct: '',
  issue_date: '', expiry_date: '', expiry_place: '',
  latest_shipment_date: '', presentation_days: '',
  port_of_loading: '', port_of_discharge: '',
  partial_shipment: 'not_allowed', transhipment: 'not_allowed',
  incoterm: '', status: 'draft', notes: '', tags: [],
};

const EMPTY_AMENDMENT = {
  amendment_date: '', requested_by: 'applicant',
  changes_description: '', new_expiry_date: '',
  new_amount: '', new_latest_shipment_date: '',
  status: 'pending', notes: '',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function lcTypeMeta(v) {
  return LC_TYPES.find(t => t.value === v) || { label: v, icon: '📄', color: '#64748b', bg: '#f8fafc', border: '#e2e8f0' };
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
}

// ── Badges ────────────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  return (
    <span style={{ padding: '2px 10px', borderRadius: 99, background: m.bg, border: `1px solid ${m.border}`, color: m.text, fontSize: 11, fontWeight: 600 }}>
      {m.label}
    </span>
  );
}

function TypeBadge({ type }) {
  const m = lcTypeMeta(type);
  return (
    <span style={{ padding: '2px 10px', borderRadius: 99, background: m.bg, border: `1px solid ${m.border}`, color: m.color, fontSize: 11, fontWeight: 600 }}>
      {m.icon} {m.label}
    </span>
  );
}

function AmendmentStatusBadge({ status }) {
  const m = AMENDMENT_STATUS_META[status] || AMENDMENT_STATUS_META.pending;
  return (
    <span style={{ padding: '2px 8px', borderRadius: 99, background: m.bg, border: `1px solid ${m.border}`, color: m.text, fontSize: 11, fontWeight: 600 }}>
      {m.label}
    </span>
  );
}

function ExpiryBadge({ expiryDate }) {
  const days = daysUntil(expiryDate);
  if (days === null) return null;
  if (days < 0)  return <span style={{ padding: '2px 8px', borderRadius: 99, background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', fontSize: 11, fontWeight: 700 }}>Abgelaufen</span>;
  if (days <= 7)  return <span style={{ padding: '2px 8px', borderRadius: 99, background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', fontSize: 11, fontWeight: 700 }}>⚠ {days}d</span>;
  if (days <= 30) return <span style={{ padding: '2px 8px', borderRadius: 99, background: '#fff7ed', border: '1px solid #fdba74', color: '#c2410c', fontSize: 11, fontWeight: 700 }}>⏰ {days}d</span>;
  return <span style={{ padding: '2px 8px', borderRadius: 99, background: '#f0fdf4', border: '1px solid #86efac', color: '#15803d', fontSize: 11, fontWeight: 600 }}>{days}d</span>;
}

// ── Field helper ──────────────────────────────────────────────────────────────

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = {
  padding: '7px 10px', borderRadius: 6,
  border: '1px solid var(--border)', background: 'var(--bg-surface)',
  color: 'var(--text1)', fontSize: 13, width: '100%', boxSizing: 'border-box',
};
const selectStyle = { ...inputStyle };

// ── Amendment Panel ───────────────────────────────────────────────────────────

function AmendmentPanel({ lcId, amendments, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState(EMPTY_AMENDMENT);
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState(null);

  function change(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function save() {
    if (!form.changes_description.trim()) { setErr('Beschreibung erforderlich'); return; }
    setSaving(true); setErr(null);
    try {
      await API.post(`/lc-sblc/${lcId}/amendments`, form);
      setForm(EMPTY_AMENDMENT);
      setShowForm(false);
      onRefresh();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  }

  async function updateStatus(amendmentId, newStatus) {
    try {
      await API.patch(`/lc-sblc/${lcId}/amendments/${amendmentId}`, { status: newStatus });
      onRefresh();
    } catch (e) { console.error(e); }
  }

  async function deleteAmendment(amendmentId) {
    if (!window.confirm('Amendment loschen?')) return;
    try {
      await API.delete(`/lc-sblc/${lcId}/amendments/${amendmentId}`);
      onRefresh();
    } catch (e) { console.error(e); }
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text1)' }}>
          Amendments ({amendments.length})
        </div>
        <button onClick={() => setShowForm(s => !s)} style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #22c55e', background: showForm ? '#22c55e' : 'transparent', color: showForm ? '#000' : '#22c55e', fontSize: 12, cursor: 'pointer' }}>
          {showForm ? 'Abbrechen' : '+ Amendment'}
        </button>
      </div>

      {showForm && (
        <div style={{ background: 'var(--bg-deep)', border: '1px solid var(--border)', borderRadius: 8, padding: 14, marginBottom: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <Field label="Datum">
              <input type="date" style={inputStyle} value={form.amendment_date} onChange={e => change('amendment_date', e.target.value)} />
            </Field>
            <Field label="Angefordert von">
              <select style={selectStyle} value={form.requested_by} onChange={e => change('requested_by', e.target.value)}>
                <option value="applicant">Applicant</option>
                <option value="beneficiary">Beneficiary</option>
                <option value="bank">Bank</option>
              </select>
            </Field>
            <Field label="Neue Laufzeit">
              <input type="date" style={inputStyle} value={form.new_expiry_date} onChange={e => change('new_expiry_date', e.target.value)} />
            </Field>
            <Field label="Neuer Betrag">
              <input style={inputStyle} placeholder="z.B. 5,000,000" value={form.new_amount} onChange={e => change('new_amount', e.target.value)} />
            </Field>
            <Field label="Neues Verschiffungsdatum">
              <input type="date" style={inputStyle} value={form.new_latest_shipment_date} onChange={e => change('new_latest_shipment_date', e.target.value)} />
            </Field>
            <Field label="Status">
              <select style={selectStyle} value={form.status} onChange={e => change('status', e.target.value)}>
                <option value="pending">Ausstehend</option>
                <option value="accepted">Akzeptiert</option>
                <option value="rejected">Abgelehnt</option>
              </select>
            </Field>
          </div>
          <Field label="Anderungsbeschreibung *">
            <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={form.changes_description} onChange={e => change('changes_description', e.target.value)} placeholder="Beschreibe die Anderungen..." />
          </Field>
          <Field label="Notizen">
            <textarea style={{ ...inputStyle, minHeight: 40, resize: 'vertical', marginTop: 8 }} value={form.notes} onChange={e => change('notes', e.target.value)} />
          </Field>
          {err && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 6 }}>{err}</div>}
          <button onClick={save} disabled={saving} style={{ marginTop: 10, padding: '6px 18px', borderRadius: 6, border: 'none', background: '#22c55e', color: '#000', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
            {saving ? 'Speichern...' : 'Amendment speichern'}
          </button>
        </div>
      )}

      {amendments.length === 0 && !showForm && (
        <div style={{ color: 'var(--text3)', fontSize: 12, fontStyle: 'italic' }}>Keine Amendments vorhanden</div>
      )}

      {amendments.map(a => (
        <div key={a.id} style={{ background: 'var(--bg-deep)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', marginBottom: 6, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ minWidth: 28, height: 28, borderRadius: '50%', background: '#22c55e', color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>
            {a.amendment_number}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
              <AmendmentStatusBadge status={a.status} />
              {a.amendment_date && <span style={{ fontSize: 11, color: 'var(--text3)' }}>{a.amendment_date}</span>}
              {a.requested_by && <span style={{ fontSize: 11, color: 'var(--text3)' }}>von: {a.requested_by}</span>}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text1)', marginBottom: 4 }}>{a.changes_description}</div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 11, color: 'var(--text3)' }}>
              {a.new_expiry_date && <span>Neue Laufzeit: {a.new_expiry_date}</span>}
              {a.new_amount && <span>Neuer Betrag: {a.new_amount}</span>}
              {a.new_latest_shipment_date && <span>Verschiffung: {a.new_latest_shipment_date}</span>}
            </div>
            {a.notes && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4, fontStyle: 'italic' }}>{a.notes}</div>}
            {a.status === 'pending' && (
              <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
                <button onClick={() => updateStatus(a.id, 'accepted')} style={{ padding: '3px 10px', borderRadius: 6, border: 'none', background: '#15803d', color: '#fff', fontSize: 11, cursor: 'pointer' }}>Akzeptieren</button>
                <button onClick={() => updateStatus(a.id, 'rejected')} style={{ padding: '3px 10px', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', fontSize: 11, cursor: 'pointer' }}>Ablehnen</button>
              </div>
            )}
          </div>
          <button onClick={() => deleteAmendment(a.id)} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 16, padding: 2, flexShrink: 0 }} title="Loschen">x</button>
        </div>
      ))}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function LcSblcPage() {
  const [records, setRecords]   = useState([]);
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [saving, setSaving]     = useState(false);
  const [formErr, setFormErr]   = useState(null);
  const [filterType, setFilterType]   = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = {};
      if (filterType)   params.lc_type = filterType;
      if (filterStatus) params.status  = filterStatus;
      const [rRes, sRes] = await Promise.all([
        API.get('/lc-sblc/', { params }),
        API.get('/lc-sblc/summary'),
      ]);
      setRecords(rRes.data);
      setSummary(sRes.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Ladefehler');
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus]);

  useEffect(() => { load(); }, [load]);

  function openCreate() {
    setEditRecord(null);
    setForm(EMPTY_FORM);
    setFormErr(null);
    setActiveTab('details');
    setShowModal(true);
  }

  function openEdit(r) {
    setEditRecord(r);
    setForm({
      lc_type: r.lc_type, lc_subtype: r.lc_subtype || 'irrevocable',
      reference_number: r.reference_number || '', deal_reference: r.deal_reference || '',
      title: r.title || '', applicant_name: r.applicant_name || '',
      beneficiary_name: r.beneficiary_name || '',
      applicant_bank: r.applicant_bank || '', applicant_bank_swift: r.applicant_bank_swift || '',
      issuing_bank: r.issuing_bank || '', issuing_bank_swift: r.issuing_bank_swift || '',
      advising_bank: r.advising_bank || '', advising_bank_swift: r.advising_bank_swift || '',
      confirming_bank: r.confirming_bank || '', confirming_bank_swift: r.confirming_bank_swift || '',
      amount: r.amount || '', currency: r.currency || 'USD', tolerance_pct: r.tolerance_pct || '',
      issue_date: r.issue_date || '', expiry_date: r.expiry_date || '',
      expiry_place: r.expiry_place || '', latest_shipment_date: r.latest_shipment_date || '',
      presentation_days: r.presentation_days || '',
      port_of_loading: r.port_of_loading || '', port_of_discharge: r.port_of_discharge || '',
      partial_shipment: r.partial_shipment || 'not_allowed',
      transhipment: r.transhipment || 'not_allowed',
      incoterm: r.incoterm || '', status: r.status || 'draft',
      notes: r.notes || '', tags: r.tags || [],
    });
    setFormErr(null);
    setActiveTab('details');
    setShowModal(true);
  }

  function change(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function save() {
    if (!form.title.trim()) { setFormErr('Titel erforderlich'); return; }
    setSaving(true); setFormErr(null);
    try {
      if (editRecord) {
        await API.patch(`/lc-sblc/${editRecord.id}`, form);
      } else {
        await API.post('/lc-sblc/', form);
      }
      setShowModal(false);
      load();
    } catch (e) {
      setFormErr(e.response?.data?.detail || 'Speicherfehler');
    } finally {
      setSaving(false);
    }
  }

  async function deleteRecord(id) {
    if (!window.confirm('LC/SBLC loschen?')) return;
    try {
      await API.delete(`/lc-sblc/${id}`);
      load();
    } catch (e) { console.error(e); }
  }

  function toggleExpand(id) {
    setExpandedId(prev => prev === id ? null : id);
  }

  return (
    <div style={{ padding: '20px 16px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text1)' }}>🏦 LC/SBLC Management</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text3)' }}>Letter of Credit & Standby LC — Tracking, Bank Details, Amendments</p>
        </div>
        <button onClick={openCreate} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#22c55e', color: '#000', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
          + Neues LC/SBLC
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
          {[
            { icon: '📊', label: 'Gesamt', value: summary.total, color: '#22c55e' },
            { icon: '🏦', label: 'LC', value: summary.by_type?.LC || 0, color: '#065f46' },
            { icon: '🏛', label: 'SBLC', value: summary.by_type?.SBLC || 0, color: '#1e40af' },
            { icon: '⏰', label: 'Lauft ab (30d)', value: summary.expiring_soon?.length || 0, color: '#c2410c' },
            { icon: '⚠', label: 'Abgelaufen', value: summary.expired?.length || 0, color: '#dc2626' },
          ].map(c => (
            <div key={c.label} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 18px', minWidth: 120 }}>
              <div style={{ fontSize: 20, marginBottom: 3 }}>{c.icon}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: c.color }}>{c.value}</div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)' }}>{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Expiry Warnings */}
      {summary?.expiring_soon?.length > 0 && (
        <div style={{ background: '#f59e0b15', border: '1px solid #f59e0b40', borderRadius: 8, padding: '10px 16px', marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#f59e0b', marginBottom: 6 }}>⏰ Lauft bald ab</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {summary.expiring_soon.map(r => (
              <span key={r.id} style={{ padding: '3px 10px', borderRadius: 99, background: '#f59e0b20', border: '1px solid #f59e0b40', fontSize: 12, color: '#f59e0b' }}>
                {r.lc_type} — {r.reference_number || r.title} — {r.expiry_date}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <select style={{ ...selectStyle, width: 'auto', minWidth: 120 }} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">Alle Typen</option>
          {LC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select style={{ ...selectStyle, width: 'auto', minWidth: 140 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">Alle Status</option>
          {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
        <button onClick={load} style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text1)', cursor: 'pointer', fontSize: 13 }}>Aktualisieren</button>
      </div>

      {/* Error */}
      {error && <div style={{ background: '#ef444415', border: '1px solid #ef444440', borderRadius: 8, padding: '10px 14px', color: '#ef4444', fontSize: 13, marginBottom: 16 }}>{error}</div>}

      {/* Loading */}
      {loading && <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Lade...</div>}

      {/* Records */}
      {!loading && records.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)', fontSize: 14 }}>
          Keine LC/SBLC gefunden. Erstelle das erste mit "+ Neues LC/SBLC".
        </div>
      )}

      {!loading && records.map(r => {
        const isExpanded = expandedId === r.id;
        return (
          <div key={r.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, marginBottom: 10, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer' }} onClick={() => toggleExpand(r.id)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 4 }}>
                  <TypeBadge type={r.lc_type} />
                  <StatusBadge status={r.status} />
                  {r.expiry_date && <ExpiryBadge expiryDate={r.expiry_date} />}
                  {r.amendment_count > 0 && (
                    <span style={{ padding: '2px 8px', borderRadius: 99, background: '#f59e0b20', border: '1px solid #f59e0b40', color: '#f59e0b', fontSize: 11, fontWeight: 600 }}>
                      {r.amendment_count} Amendment{r.amendment_count > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text1)', marginBottom: 2 }}>{r.title}</div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: 'var(--text2)' }}>
                  {r.reference_number && <span>Ref: {r.reference_number}</span>}
                  {r.deal_reference && <span>Deal: {r.deal_reference}</span>}
                  {r.amount && <span>{r.amount} {r.currency}</span>}
                  {r.issuing_bank && <span>🏦 {r.issuing_bank}</span>}
                  {r.expiry_date && <span>Lauft ab: {r.expiry_date}</span>}
                  {r.beneficiary_name && <span>Beneficiary: {r.beneficiary_name}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button onClick={e => { e.stopPropagation(); openEdit(r); }} style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text1)', fontSize: 12, cursor: 'pointer' }}>✏</button>
                <button onClick={e => { e.stopPropagation(); deleteRecord(r.id); }} style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #ef444440', background: '#ef444415', color: '#ef4444', fontSize: 12, cursor: 'pointer' }}>🗑</button>
                <span style={{ fontSize: 16, color: 'var(--text3)', padding: '4px 6px' }}>{isExpanded ? '▲' : '▼'}</span>
              </div>
            </div>

            {/* Expanded Detail */}
            {isExpanded && (
              <div style={{ borderTop: '1px solid var(--border)', padding: '14px 16px' }}>
                {/* Tab Bar */}
                <div style={{ display: 'flex', gap: 4, marginBottom: 14, borderBottom: '1px solid var(--border)', paddingBottom: 8 }}>
                  {[
                    { key: 'details', label: 'Details' },
                    { key: 'bank', label: 'Bankdaten' },
                    { key: 'trade', label: 'Handelsbed.' },
                    { key: 'amendments', label: `Amendments (${r.amendments?.length || 0})` },
                    { key: 'attachments', label: 'Anhänge' },
                  ].map(tab => (
                    <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                      style={{ padding: '4px 12px', borderRadius: 6, border: 'none', background: activeTab === tab.key ? '#22c55e' : 'transparent', color: activeTab === tab.key ? '#000' : 'var(--text2)', fontWeight: activeTab === tab.key ? 600 : 400, fontSize: 12, cursor: 'pointer' }}>
                      {tab.label}
                    </button>
                  ))}
                </div>

                {activeTab === 'details' && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                    {[
                      ['Typ', r.lc_type + (r.lc_subtype ? ` / ${r.lc_subtype}` : '')],
                      ['Referenz', r.reference_number],
                      ['Deal Referenz', r.deal_reference],
                      ['Applicant', r.applicant_name],
                      ['Beneficiary', r.beneficiary_name],
                      ['Betrag', r.amount ? `${r.amount} ${r.currency}` : null],
                      ['Toleranz', r.tolerance_pct ? `+/-${r.tolerance_pct}%` : null],
                      ['Ausstellungsdatum', r.issue_date],
                      ['Ablaufdatum', r.expiry_date],
                      ['Ablaufort', r.expiry_place],
                      ['Spateste Verschiffung', r.latest_shipment_date],
                      ['Prasentationsfrist', r.presentation_days ? `${r.presentation_days} Tage` : null],
                    ].filter(([, v]) => v).map(([k, v]) => (
                      <div key={k}>
                        <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{k}</div>
                        <div style={{ fontSize: 13, color: 'var(--text1)', fontWeight: 500 }}>{v}</div>
                      </div>
                    ))}
                    {r.notes && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Notizen</div>
                        <div style={{ fontSize: 13, color: 'var(--text1)' }}>{r.notes}</div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'bank' && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                    {[
                      ['Applicant Bank', r.applicant_bank, r.applicant_bank_swift],
                      ['Issuing Bank', r.issuing_bank, r.issuing_bank_swift],
                      ['Advising Bank', r.advising_bank, r.advising_bank_swift],
                      ['Confirming Bank', r.confirming_bank, r.confirming_bank_swift],
                    ].filter(([, v]) => v).map(([k, v, swift]) => (
                      <div key={k} style={{ background: 'var(--bg-deep)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px' }}>
                        <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{k}</div>
                        <div style={{ fontSize: 13, color: 'var(--text1)', fontWeight: 600 }}>{v}</div>
                        {swift && <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2, fontFamily: 'monospace' }}>SWIFT: {swift}</div>}
                      </div>
                    ))}
                    {!r.applicant_bank && !r.issuing_bank && !r.advising_bank && !r.confirming_bank && (
                      <div style={{ color: 'var(--text3)', fontSize: 13, fontStyle: 'italic' }}>Keine Bankdaten erfasst.</div>
                    )}
                  </div>
                )}

                {activeTab === 'trade' && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                    {[
                      ['Verladehafen', r.port_of_loading],
                      ['Loschhafen', r.port_of_discharge],
                      ['Incoterm', r.incoterm],
                      ['Teilverladung', r.partial_shipment === 'allowed' ? 'Erlaubt' : 'Nicht erlaubt'],
                      ['Umladung', r.transhipment === 'allowed' ? 'Erlaubt' : 'Nicht erlaubt'],
                    ].filter(([, v]) => v).map(([k, v]) => (
                      <div key={k}>
                        <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{k}</div>
                        <div style={{ fontSize: 13, color: 'var(--text1)', fontWeight: 500 }}>{v}</div>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'amendments' && (
                  <AmendmentPanel lcId={r.id} amendments={r.amendments || []} onRefresh={load} />
                )}

                {activeTab === 'attachments' && (
                  <AttachmentsWidget entityType="lc_sblc" entityId={r.id} />
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, width: '100%', maxWidth: 700, maxHeight: '90vh', overflow: 'auto' }}>
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 1 }}>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text1)' }}>
                {editRecord ? 'LC/SBLC bearbeiten' : 'Neues LC/SBLC erstellen'}
              </h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--text3)', cursor: 'pointer', lineHeight: 1 }}>x</button>
            </div>

            {/* Modal Tabs */}
            <div style={{ display: 'flex', gap: 4, padding: '10px 20px 0', borderBottom: '1px solid var(--border)' }}>
              {[
                { key: 'details', label: 'Grunddaten' },
                { key: 'bank', label: 'Bankdaten' },
                { key: 'trade', label: 'Handelsbedingungen' },
              ].map(tab => (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                  style={{ padding: '6px 14px', borderRadius: '6px 6px 0 0', border: 'none', borderBottom: activeTab === tab.key ? '2px solid #22c55e' : '2px solid transparent', background: 'transparent', color: activeTab === tab.key ? '#22c55e' : 'var(--text2)', fontWeight: activeTab === tab.key ? 600 : 400, fontSize: 13, cursor: 'pointer' }}>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Modal Body */}
            <div style={{ padding: '16px 20px' }}>

              {activeTab === 'details' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Typ *">
                    <select style={selectStyle} value={form.lc_type} onChange={e => change('lc_type', e.target.value)}>
                      {LC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Subtyp">
                    <select style={selectStyle} value={form.lc_subtype} onChange={e => change('lc_subtype', e.target.value)}>
                      <option value="">— wahlen —</option>
                      {LC_SUBTYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Titel *">
                    <input style={inputStyle} value={form.title} onChange={e => change('title', e.target.value)} placeholder="z.B. LC fur Crude Oil Lieferung" />
                  </Field>
                  <Field label="Status">
                    <select style={selectStyle} value={form.status} onChange={e => change('status', e.target.value)}>
                      {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Referenznummer">
                    <input style={inputStyle} value={form.reference_number} onChange={e => change('reference_number', e.target.value)} placeholder="LC-2024-001" />
                  </Field>
                  <Field label="Deal Referenz">
                    <input style={inputStyle} value={form.deal_reference} onChange={e => change('deal_reference', e.target.value)} placeholder="DEAL-2024-001" />
                  </Field>
                  <Field label="Applicant (Kaufer)">
                    <input style={inputStyle} value={form.applicant_name} onChange={e => change('applicant_name', e.target.value)} />
                  </Field>
                  <Field label="Beneficiary (Verkaufer)">
                    <input style={inputStyle} value={form.beneficiary_name} onChange={e => change('beneficiary_name', e.target.value)} />
                  </Field>
                  <Field label="Betrag">
                    <input style={inputStyle} value={form.amount} onChange={e => change('amount', e.target.value)} placeholder="5,000,000" />
                  </Field>
                  <Field label="Wahrung">
                    <select style={selectStyle} value={form.currency} onChange={e => change('currency', e.target.value)}>
                      {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label="Toleranz (%)">
                    <input style={inputStyle} value={form.tolerance_pct} onChange={e => change('tolerance_pct', e.target.value)} placeholder="5" />
                  </Field>
                  <Field label="Prasentationsfrist (Tage)">
                    <input style={inputStyle} value={form.presentation_days} onChange={e => change('presentation_days', e.target.value)} placeholder="21" />
                  </Field>
                  <Field label="Ausstellungsdatum">
                    <input type="date" style={inputStyle} value={form.issue_date} onChange={e => change('issue_date', e.target.value)} />
                  </Field>
                  <Field label="Ablaufdatum">
                    <input type="date" style={inputStyle} value={form.expiry_date} onChange={e => change('expiry_date', e.target.value)} />
                  </Field>
                  <Field label="Ablaufort">
                    <input style={inputStyle} value={form.expiry_place} onChange={e => change('expiry_place', e.target.value)} placeholder="z.B. Rotterdam" />
                  </Field>
                  <Field label="Spateste Verschiffung">
                    <input type="date" style={inputStyle} value={form.latest_shipment_date} onChange={e => change('latest_shipment_date', e.target.value)} />
                  </Field>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <Field label="Notizen">
                      <textarea style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} value={form.notes} onChange={e => change('notes', e.target.value)} />
                    </Field>
                  </div>
                </div>
              )}

              {activeTab === 'bank' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Applicant Bank"><input style={inputStyle} value={form.applicant_bank} onChange={e => change('applicant_bank', e.target.value)} /></Field>
                  <Field label="Applicant Bank SWIFT"><input style={inputStyle} value={form.applicant_bank_swift} onChange={e => change('applicant_bank_swift', e.target.value)} placeholder="XXXXDE..." /></Field>
                  <Field label="Issuing Bank"><input style={inputStyle} value={form.issuing_bank} onChange={e => change('issuing_bank', e.target.value)} /></Field>
                  <Field label="Issuing Bank SWIFT"><input style={inputStyle} value={form.issuing_bank_swift} onChange={e => change('issuing_bank_swift', e.target.value)} placeholder="XXXXDE..." /></Field>
                  <Field label="Advising Bank"><input style={inputStyle} value={form.advising_bank} onChange={e => change('advising_bank', e.target.value)} /></Field>
                  <Field label="Advising Bank SWIFT"><input style={inputStyle} value={form.advising_bank_swift} onChange={e => change('advising_bank_swift', e.target.value)} placeholder="XXXXDE..." /></Field>
                  <Field label="Confirming Bank"><input style={inputStyle} value={form.confirming_bank} onChange={e => change('confirming_bank', e.target.value)} /></Field>
                  <Field label="Confirming Bank SWIFT"><input style={inputStyle} value={form.confirming_bank_swift} onChange={e => change('confirming_bank_swift', e.target.value)} placeholder="XXXXDE..." /></Field>
                </div>
              )}

              {activeTab === 'trade' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Verladehafen"><input style={inputStyle} value={form.port_of_loading} onChange={e => change('port_of_loading', e.target.value)} placeholder="z.B. Bonny Terminal" /></Field>
                  <Field label="Loschhafen"><input style={inputStyle} value={form.port_of_discharge} onChange={e => change('port_of_discharge', e.target.value)} placeholder="z.B. Rotterdam" /></Field>
                  <Field label="Incoterm">
                    <select style={selectStyle} value={form.incoterm} onChange={e => change('incoterm', e.target.value)}>
                      <option value="">— wahlen —</option>
                      {INCOTERMS.map(i => <option key={i} value={i}>{i}</option>)}
                    </select>
                  </Field>
                  <Field label="Teilverladung">
                    <select style={selectStyle} value={form.partial_shipment} onChange={e => change('partial_shipment', e.target.value)}>
                      <option value="not_allowed">Nicht erlaubt</option>
                      <option value="allowed">Erlaubt</option>
                    </select>
                  </Field>
                  <Field label="Umladung">
                    <select style={selectStyle} value={form.transhipment} onChange={e => change('transhipment', e.target.value)}>
                      <option value="not_allowed">Nicht erlaubt</option>
                      <option value="allowed">Erlaubt</option>
                    </select>
                  </Field>
                </div>
              )}

              {formErr && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>{formErr}</div>}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                <button onClick={() => setShowModal(false)} style={{ padding: '8px 18px', borderRadius: 7, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text2)', cursor: 'pointer', fontSize: 13 }}>Abbrechen</button>
                <button onClick={save} disabled={saving} style={{ padding: '8px 18px', borderRadius: 7, border: 'none', background: saving ? '#16a34a' : '#22c55e', color: '#000', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                  {saving ? 'Speichern...' : 'Speichern'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
